//External imports
import React, { useState, useEffect } from "react";

import {
  Paper,
  Typography,
  Autocomplete,
  TextField,
  LinearProgress,
} from "@mui/material";
import {
  getPoliceService,
  patchPoliceService,
  PoliceServiceModel,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

// Internal imports
import { validateEmail } from "@/common/utils";

import styles from "./SettingsPage.module.css";

interface IAddEmailOption {
  inputValue: string;
  label: string;
}

const SettingsPage = () => {
  const [policeService, setPoliceService] = useState<PoliceServiceModel | null>(
    null,
  );

  const isLoading = policeService === null;
  const selectedEmails = policeService?.notify.emails ?? null;

  useEffect(() => {
    const fetchData = async () => {
      setPoliceService((await getPoliceService()).data!);
    };

    fetchData();
  }, []);

  const handleEmailChange = async (
    _event: React.ChangeEvent<{}>,
    newValues: (string | IAddEmailOption)[],
  ) => {
    let emails: string[] = [];

    const currentEmails = selectedEmails ?? [];
    if (newValues.length > currentEmails.length) {
      //A new email is added
      const lastValue = newValues[newValues.length - 1];
      const newEmail =
        typeof lastValue === "string" ? lastValue : lastValue.inputValue;

      if (!validateEmail(newEmail)) {
        toast.error("Invalid email format!");
        return;
      }

      if (currentEmails.some((email) => email === newEmail)) {
        toast.warning(`${newEmail} already exists`);
        return;
      }
    }

    emails = [
      ...newValues.map((v) => (typeof v === "string" ? v : v.inputValue)),
    ];

    const newData = {
      notify: {
        emails: emails,
      },
    };

    patchPoliceService({ body: newData });
  };

  return (
    <div className={styles.formContainer}>
      <Paper elevation={3} className={styles.sectionContainer}>
        <Typography variant="h6">
          <b>Notification Setting</b>
        </Typography>

        <Typography variant="body2" gutterBottom>
          Who should receive an email notification when a new public camera
          registration is made?
        </Typography>

        {!selectedEmails ? (
          <LinearProgress sx={{ width: "100%" }} />
        ) : (
          <Autocomplete
            fullWidth
            multiple
            options={[]}
            id="email-autocomplete"
            value={selectedEmails}
            onChange={handleEmailChange}
            freeSolo
            loading={isLoading}
            filterOptions={(_options, params) => {
              const filtered: string[] = [];
              if (params.inputValue !== "") {
                filtered.push(params.inputValue);
              }

              return filtered;
            }}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Emails"
                placeholder="Add email..."
              />
            )}
          />
        )}
      </Paper>
    </div>
  );
};

export default SettingsPage;
